<template>
  <div
    class="media"
    ref="mediaEl"
    :class="{
      'image-loaded': imageLoaded,
      'video-playing': videoPlaying,
      'video-loading': showVideoLoader && isVideoLoading,
    }"
  >
    <img v-if="src" :src="src" @load="onLoad" draggable="false" />
    <video
      v-if="video"
      ref="videoEl"
      loop
      muted
      playsinline
      disablepictureinpicture
      disableremoteplayback
      controlslist="nodownload noplaybackrate"
      preload="none"
      @loadeddata="onVideoReady"
      @waiting="onVideoWaiting"
      @playing="onVideoPlaying"
      @error="onVideoError"
    >
      <source v-if="videoWebm" :src="videoWebm" type="video/webm" />
      <source :src="video" type="video/mp4" />
    </video>

    <div
      class="media__loader"
      :aria-hidden="!isVideoLoading"
      v-if="video && showVideoLoader"
    >
      <span class="media__loader-spinner"></span>
      <span class="media__loader-gradient"></span>
    </div>
  </div>
</template>

<script setup>
const imageLoaded = ref(false);
const videoPlaying = ref(false);
const isVideoLoading = ref(false);
const mediaEl = ref(null);
const videoEl = ref(null);
const { src, video, videoWebm, autoplay, showVideoLoader } = defineProps({
  src: String,
  video: String,
  videoWebm: String,
  autoplay: { type: Boolean, default: true },
  showVideoLoader: { type: Boolean, default: false },
});

let observer = null;

const onLoad = () => {
  imageLoaded.value = true;
};

const onVideoReady = () => {
  isVideoLoading.value = false;
  videoPlaying.value = true;
};

const onVideoWaiting = () => {
  isVideoLoading.value = true;
};

const onVideoPlaying = () => {
  isVideoLoading.value = false;
  videoPlaying.value = true;
};

const onVideoError = () => {
  isVideoLoading.value = false;
};

const handleIntersection = entries => {
  entries.forEach(entry => {
    if (!videoEl.value || !autoplay) return;

    if (entry.isIntersecting) {
      // Video entering viewport - load and play
      if (videoEl.value.readyState === 0) {
        isVideoLoading.value = true;
        videoEl.value.load();
      }
      videoEl.value.play().catch(err => {
        console.warn('Video autoplay prevented:', err);
      });
    } else {
      // Video leaving viewport - pause to save resources
      videoEl.value.pause();
    }
  });
};

onMounted(() => {
  if (video && mediaEl.value && autoplay) {
    observer = new IntersectionObserver(handleIntersection, {
      threshold: 0.2,
      rootMargin: '100px',
    });

    observer.observe(mediaEl.value);
  }
});

onUnmounted(() => {
  if (observer) {
    observer.disconnect();
    observer = null;
  }

  if (videoEl.value) {
    isVideoLoading.value = false;
    videoEl.value.pause();
    videoEl.value.src = '';
    videoEl.value.load();
  }
});
</script>

<style lang="scss">
.media {
  position: relative;
  overflow: hidden;

  img,
  video {
    width: 100%;
    height: auto;
    display: block;
  }

  img {
    transition: opacity 0.3s ease 0.3s;
  }

  video {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  .media__loader {
    position: absolute;
    right: 0;
    bottom: 0;
    width: 25rem;
    aspect-ratio: 1;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.32s ease;
  }

  .media__loader-gradient,
  .media__loader-spinner {
    position: absolute;
  }

  .media__loader-gradient {
    width: 100%;
    height: 100%;
    z-index: 0;
    background: linear-gradient(
      135deg,
      rgba(0, 0, 0, 0) 50%,
      rgba(0, 0, 0, 0.7) 200%
    );
    backdrop-filter: blur(15px);
    mask-image: linear-gradient(
      135deg,
      rgba(0, 0, 0, 0) 50%,
      rgba(0, 0, 0, 1) 80%
    );
  }

  .media__loader-spinner {
    right: var(--gutter);
    bottom: var(--gutter);
    width: 16px;
    aspect-ratio: 1;
    border-radius: 50%;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-right-color: rgba(255, 255, 255, 0.3);
    border-left-color: rgba(255, 255, 255, 1);
    border-top-color: rgba(255, 255, 255, 1);
    animation: media-spinner-rotate 0.85s linear infinite;
    z-index: 1;

    @include mq(tablet) {
      right: var(--gutter-double);
      bottom: var(--gutter-double);
    }
  }

  &.video-loading {
    .media__loader {
      opacity: 1;
    }
  }

  &.video-playing {
    img {
      opacity: 0;
    }

    video {
      opacity: 1;
    }
  }
}

@keyframes media-spinner-rotate {
  to {
    transform: rotate(360deg);
  }
}
</style>
